import React, { useEffect, useState } from "react";
import {
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import country from "country-list-js";
import { CloseRounded } from "@mui/icons-material";
import { StyledModal } from "../StyledComponents/StyledModal/StyledModal";
import pp_logo from "../../../assets/pp-logo-new.svg";

interface CountrySelectorDialogProps {
  open: boolean;
  onClose: (country: { label: string; value: string } | null) => void;
}

const CountrySelectorDialog: React.FC<CountrySelectorDialogProps> = ({
  open,
  onClose,
}) => {
  // Extract all countries with names and codes
  const allCountries = Object.entries(
    country.names().reduce((acc, name) => {
      const code = country.findByName(name).code.iso2; // Get ISO2 code for the country
      acc[name] = code;
      return acc;
    }, {} as Record<string, string>)
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCountries, setFilteredCountries] = useState(
    allCountries.sort()
  );

  useEffect(() => {
    setSearchTerm("");
    setFilteredCountries(allCountries.sort());
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const search = e.target.value.toLowerCase();
    setSearchTerm(search);

    setFilteredCountries(
      allCountries.filter(([name]) => name.toLowerCase().includes(search))
    );
  };

  const handleClose = () => {
    setSearchTerm("");
    onClose(null);
  };

  const handleSelectCountry = ([name, code]: [string, any]) => {
    setSearchTerm("");
    setFilteredCountries(allCountries.sort());
    onClose({ label: name, value: code }); // Pass the country name and code
  };

  return (
    <StyledModal open={open} onClose={handleClose}>
      <Box className="login-container">
        <Grid container display="flex" flexDirection="column" paddingTop={1}>
          <Grid item xs={12} md={12} lg={12} marginBottom={1}>
            <div className="flex-row">
              <IconButton
                disableRipple
                aria-label="price-pinger-logo-label"
                sx={{
                  padding: 0,
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                <img
                  id="price-pinger-logo"
                  alt="Price Pinger Logo"
                  src={pp_logo}
                  height={"31px"}
                  width={"31px"}
                />
              </IconButton>
              <Typography className="welcome-text" gutterBottom>
                Select the Country
              </Typography>
              <IconButton
                disableRipple
                aria-label="close modal"
                sx={{
                  padding: 0,
                  "&:hover": { backgroundColor: "transparent" },
                }}
                onClick={handleClose}
              >
                <CloseRounded />
              </IconButton>
            </div>
          </Grid>
          <Grid item xs={12} md={12} lg={12} marginBottom={1}>
            <TextField
              fullWidth
              placeholder="Search countries..."
              value={searchTerm}
              onChange={handleSearch}
              variant="outlined"
              margin="dense"
              sx={{
                "& input": {
                  padding: "8px 14px",
                  fontFamily: "Poppins Regular !important",
                },
              }}
            />
            <Box
              sx={{
                maxHeight: 300, // Limit the height of the list
                overflowY: "auto", // Enable scrolling when content overflows
                marginTop: 1,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#737ddd",
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "#555",
                  },
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                },
              }}
            >
              <List>
                {filteredCountries.map(([name, code]) => (
                  <ListItem key={`country-code-${code}`} disablePadding>
                    <ListItemButton
                      sx={{ paddingTop: 0, paddingBottom: 0 }}
                      onClick={() => handleSelectCountry([name, code])}
                    >
                      <ListItemText primary={`${name} (${code})`} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </StyledModal>
  );
};

export default CountrySelectorDialog;
